import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

type AssetType = 'image' | 'video';
type DataType = 'base64' | 'url' | 'blob';
interface AssetConfig {
  data: string;
  dataType: DataType;
  type: AssetType;
}

interface DocConfig {
  asset: AssetConfig;
  canvasSize?: {
    width: number;
    height: number;
    unit: string;
  };
}

interface EditorCallbacks {
  onCancel: () => void;
  onPublish: (intent: any, publishParams: any) => void;
  onError: (err: any) => void;
}

@Injectable({
  providedIn: 'root'
})
export class ExpressSdkService {
  private expressSdkInstance: any = null;
  private initializationPromise: Promise<void> | null = null;

  constructor() {
    this.initializeExpressSdk();
  }

  /**
   * Adobe Express SDKを初期化する
   */
  async initializeExpressSdk(): Promise<void> {
    if (this.expressSdkInstance) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization();

    try {
      await this.initializationPromise;
    } catch (error) {
      this.initializationPromise = null;
      throw error;
    }
  }

  private async performInitialization(): Promise<void> {
    if (!window.CCEverywhere) {
      throw new Error("Adobe Express SDK がロードされていません。");
    }

    const configParams = { loginMode: 'delayed', locale: "ja_JP" };

    try {
      this.expressSdkInstance = await window.CCEverywhere.initialize({
        clientId: environment.adobe.express.clientId,
        appName: environment.adobe.express.name,
      }, configParams);

      console.log("Adobe Express SDK が正常に初期化されました:", this.expressSdkInstance);
    } catch (error) {
      console.error("SDK の初期化に失敗しました:", error);
      throw error;
    }
  }

  /**
   * Adobe Expressエディターを開く汎用メソッド
   * @param assetData アセットのデータ（URLまたはbase64）
   * @param assetType アセットのタイプ（image/video）
   * @param dataType データのタイプ（url/base64）
   * @param canvasSize オプショナル：キャンバスサイズ
   */
  private async openEditor(
    assetData: string,
    assetType: AssetType,
    dataType: DataType = 'url',
    canvasSize?: { width: number; height: number; unit: string }
  ): Promise<string> {
    await this.initializeExpressSdk();

    if (!this.expressSdkInstance?.editor?.createWithAsset) {
      throw new Error("Adobe Express SDK の `createWithAsset` メソッドが利用できません。");
    }

    if (!assetData) {
      throw new Error(`編集する${assetType === 'image' ? '画像' : '動画'}がありません。`);
    }

    return new Promise((resolve, reject) => {
      const docConfig: DocConfig = {
        asset: {
          data: assetData,
          dataType: dataType,
          type: assetType
        }
      };

      if (canvasSize) {
        docConfig.canvasSize = canvasSize;
      }

      const callbacks: EditorCallbacks = {
        onCancel: () => {
          reject(new Error('編集がキャンセルされました'));
        },
        onPublish: (intent: any, publishParams: any) => {
          resolve(publishParams.asset[0].data);
        },
        onError: (err: any) => {
          reject(err);
        }
      };

      const appConfig = { callbacks };

      try {
        this.expressSdkInstance.editor.createWithAsset(docConfig, appConfig);
      } catch (error) {
        console.error(`${assetType}エディターの起動に失敗しました:`, error);
        reject(error);
      }
    });
  }

  /**
   * 画像を編集する（既存のメソッドを更新）
   * @param sceneId シーンID（ログ用）
   * @param imageUrl 画像のURL
   */
  async openExpressEditor(sceneId: number, imageUrl: string): Promise<string> {
    console.log(`Adobe Express SDK に画像を送信: Scene ID ${sceneId}`);

    const canvasSize = {
      width: 1920,
      height: 1080,
      unit: "px"
    };

    return this.openEditor(imageUrl, 'image', 'base64', canvasSize);
  }

  /**
   * 動画を編集する
   * @param videoUrl 動画のURL
   */
  async openExpressEditorForVideo(videoUrl: any): Promise<string> {
    console.log("Adobe Express SDK に動画を送信");

    const canvasSize = {
      width: 1920,
      height: 1080,
      unit: "px"
    };

    return this.openEditor(videoUrl, 'video', 'base64', canvasSize);
  }

  /**
   * SDKが初期化されているかどうかを確認する
   */
  isInitialized(): boolean {
    return !!this.expressSdkInstance;
  }

  /**
   * SDKインスタンスを取得する（デバッグ用）
   */
  getSdkInstance(): any {
    return this.expressSdkInstance;
  }
}